import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import azureDevOpsService from './services/azureDevOpsService';
import { REPOSITORIES } from './KpiPullRequests';

const REPO_STORAGE_KEY = 'kpi-open-pr-repo-filter';
const DAY_MS = 24 * 60 * 60 * 1000;

function readStoredRepoFilter() {
  try {
    return sessionStorage.getItem(REPO_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function ageInDays(date) {
  return Math.floor((Date.now() - date.getTime()) / DAY_MS);
}

export default function KpiOpenPullRequests() {
  const [repoFilter, setRepoFilter] = useState(readStoredRepoFilter);
  const [stats, setStats] = useState([]);
  const [totalPrs, setTotalPrs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPerson, setExpandedPerson] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function analyzeOpenPullRequests() {
      setLoading(true);
      setError(null);
      setStats([]);
      setTotalPrs(0);
      setExpandedPerson(null);

      try {
        const allRepos = await azureDevOpsService.getRepositories();
        let targetRepos = allRepos.filter(repo =>
          REPOSITORIES.some(name => repo.name.toLowerCase() === name.toLowerCase())
        );
        if (repoFilter) {
          targetRepos = targetRepos.filter(r => r.name.toLowerCase() === repoFilter);
        }
        if (targetRepos.length === 0) {
          if (!cancelled) setError('Aucun repository trouvé pour ce filtre');
          return;
        }

        const prsByRepo = await Promise.all(
          targetRepos.map(repo =>
            azureDevOpsService.getPullRequestsByRepository(repo.id, null, 'active')
              .then(prs => prs.map(pr => ({ pr, repo })))
          )
        );
        if (cancelled) return;

        // PR ouvertes = statut active, hors draft
        const openPrs = prsByRepo.flat().filter(({ pr }) => !pr.isDraft);

        const personMap = new Map();
        for (const { pr, repo } of openPrs) {
          const name = pr.createdBy?.displayName || 'Inconnu';
          if (!personMap.has(name)) {
            personMap.set(name, { name, email: pr.createdBy?.uniqueName || '', count: 0, repos: new Set(), items: [] });
          }
          const person = personMap.get(name);
          person.count++;
          person.repos.add(repo.name);
          person.items.push({
            id: pr.pullRequestId,
            title: pr.title,
            repo: repo.name,
            date: new Date(pr.creationDate),
            url: `https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_git/${repo.name}/pullrequest/${pr.pullRequestId}`
          });
        }

        const sorted = Array.from(personMap.values())
          .map(person => ({
            ...person,
            repos: Array.from(person.repos),
            items: person.items.sort((a, b) => a.date - b.date)
          }))
          .sort((a, b) => b.count - a.count);

        setStats(sorted);
        setTotalPrs(openPrs.length);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur lors de l\'analyse des pull requests ouvertes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    analyzeOpenPullRequests();
    return () => { cancelled = true; };
  }, [repoFilter]);

  const handleRepoFilterChange = (e) => {
    const value = e.target.value;
    setRepoFilter(value);
    try {
      sessionStorage.setItem(REPO_STORAGE_KEY, value);
    } catch {
      // stockage indisponible : le filtre reste valable pour la session courante
    }
  };

  const toggleExpanded = (name) => {
    setExpandedPerson(prev => (prev === name ? null : name));
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-bold mb-4">Pull Requests ouvertes</h2>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repository
            </label>
            <select
              value={repoFilter}
              onChange={handleRepoFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les repositories</option>
              {REPOSITORIES.map((name) => (
                <option key={name} value={name.toLowerCase()}>{name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Récupération des pull requests ouvertes...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && !error && stats.length > 0 && (
        <div>
          {/* Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-600">Total PR ouvertes</div>
                <div className="text-3xl font-bold text-blue-600">{totalPrs}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Auteurs</div>
                <div className="text-3xl font-bold text-gray-900">{stats.length}</div>
              </div>
            </div>
          </div>

          {/* Stats Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auteur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PR ouvertes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Repositories
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.map((person, idx) => {
                  const isExpanded = expandedPerson === person.name;
                  return (
                    <React.Fragment key={person.name}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleExpanded(person.name)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{person.name}</div>
                          {person.email && (
                            <div className="text-xs text-gray-500">{person.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                            {person.count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex flex-wrap gap-1">
                            {person.repos.map(repo => (
                              <span key={repo} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                                {repo}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-4 py-3">
                            <ul className="space-y-1">
                              {person.items.map(item => (
                                <li key={`${item.repo}-${item.id}`} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                                  <span className="text-gray-500 whitespace-nowrap">
                                    {item.date.toLocaleDateString('fr-FR')}
                                  </span>
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline whitespace-nowrap"
                                  >
                                    !{item.id}
                                  </a>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">{item.repo}</span>
                                  <span className="text-gray-800 flex-1 min-w-0 truncate">{item.title}</span>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    ouverte depuis {ageInDays(item.date)} j
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && stats.length === 0 && (
        <div className="text-center py-6 text-gray-600">
          Aucune pull request ouverte.
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        <p>PR au statut <em>active</em>, hors brouillons (draft).</p>
      </div>
    </div>
  );
}
